import { InvoiceStatus, InvoiceType } from '../enums/invoice-status.enum';

/**
 * PD-16919 — the rule for `client.conversionDate`, the "Became Customer" date.
 *
 * The KPI Overview's New Customers card and the User Activity report's
 * Customer Conversions column both count customers whose conversion date falls
 * in the period. Until PD-16919 the date was only ever written by a workflow
 * stored in some tenant databases (mover-scripts PD-11251 / PD-11509, trigger
 * group "Client Conversion Date"): on every invoice insert it copies that
 * invoice's `date` onto the client when the client's date is still empty. A
 * tenant without that workflow, such as Macomb, never had a date, so both
 * numbers read zero there. Workd now sets it itself, for every tenant:
 *
 * - A lead converted to a customer through the CRM gets the date at that
 *   moment: its first invoice date when it already has an invoice, otherwise
 *   the day of the conversion (ClientConversionDateSubscriber).
 * - Every other customer still missing a date gets its first invoice date,
 *   from a daily job (ClientConversionDateService) and, once, from the
 *   BackfillClientConversionDate migration. This covers customers created
 *   directly as customers and every client written by an ERP integration
 *   through the legacy object API, which crm-api never sees. A customer
 *   created directly as a customer is NOT dated on creation: importing a list
 *   of existing accounts would otherwise count every one of them as a new
 *   customer that day. Its first invoice is when it actually started buying.
 *
 * An existing date is never changed, so the old workflow and Workd can both
 * run on one tenant without fighting: whichever fills the empty value first
 * wins. Nothing here changes `leadOrCustomer`; the old workflow never did
 * either.
 *
 * Which invoices count toward "first invoice": live (not soft-deleted) rows
 * with a date, excluding cancelled invoices (as the PD-11509 backfill did)
 * and credit memos (a credit on its own does not make an account a customer).
 */

/** Invoice status that never counts as a first invoice. */
export const CONVERSION_DATE_EXCLUDED_INVOICE_STATUS: string =
	InvoiceStatus.CANCELLED;

/** Invoice type that never counts as a first invoice. */
export const CONVERSION_DATE_EXCLUDED_INVOICE_TYPE: string =
	InvoiceType.CREDIT_MEMO;

/**
 * SQL predicate for an invoice that counts toward a client's first invoice
 * date, on the invoice table aliased `alias`. The values are code constants,
 * not input, so they are inlined. A NULL status or type counts, because some
 * ERP imports leave them empty.
 */
export function conversionDateInvoicePredicateSql(alias: string): string {
	return [
		`${alias}._deleted_date IS NULL`,
		`${alias}.date IS NOT NULL`,
		`(${alias}.status IS NULL OR ${alias}.status <> '${CONVERSION_DATE_EXCLUDED_INVOICE_STATUS}')`,
		`(${alias}.type IS NULL OR ${alias}.type <> '${CONVERSION_DATE_EXCLUDED_INVOICE_TYPE}')`,
	].join(' AND ');
}

/**
 * Whether a `leadOrCustomer` value means customer. Compared without case,
 * because some imports store the value in lower case.
 */
export function isCustomerRecordType(
	leadOrCustomer: string | null | undefined,
): boolean {
	return (
		leadOrCustomer != null && leadOrCustomer.trim().toLowerCase() === 'customer'
	);
}

/** The parts of a client the stamping decision reads. */
export interface ClientConversionStateModel {
	leadOrCustomer: string | null | undefined;
	conversionDate: string | null | undefined;
}

/**
 * Whether saving `next` over the stored row `stored` turns a non-customer
 * into a customer that has no conversion date yet, which is when the CRM
 * stamps one.
 *
 * `next` holds what the save writes; a field it leaves `undefined` keeps the
 * stored value. A client that was already a customer is left to the daily
 * job, so an ordinary edit of an ERP customer never pays for an invoice
 * lookup.
 */
export function shouldStampConversionDate(
	stored: ClientConversionStateModel,
	next: Partial<ClientConversionStateModel>,
): boolean {
	const nextType =
		next.leadOrCustomer !== undefined
			? next.leadOrCustomer
			: stored.leadOrCustomer;
	const nextDate =
		next.conversionDate !== undefined
			? next.conversionDate
			: stored.conversionDate;

	return (
		isCustomerRecordType(nextType) &&
		!isCustomerRecordType(stored.leadOrCustomer) &&
		(nextDate == null || nextDate === '')
	);
}

/**
 * A DATE value as `YYYY-MM-DD`. The MySQL driver returns DATE columns either
 * as a string or as a Date at local midnight, depending on connection
 * options, so both are accepted. Anything else, or an unparseable value, is
 * null.
 */
export function toSqlDateString(value: unknown): string | null {
	if (value instanceof Date) {
		if (Number.isNaN(value.getTime())) {
			return null;
		}
		const year = String(value.getFullYear()).padStart(4, '0');
		const month = String(value.getMonth() + 1).padStart(2, '0');
		const day = String(value.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}
	if (typeof value === 'string') {
		const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
		return match != null ? `${match[1]}-${match[2]}-${match[3]}` : null;
	}
	return null;
}

/**
 * The calendar date at `now` for a user whose clock is `offsetMinutes`
 * behind UTC (the sign of JavaScript's `Date.getTimezoneOffset`, so 300 is
 * US Eastern standard time), as `YYYY-MM-DD`. Used as the conversion date
 * when a lead converts before it has any invoice, so the date is the day the
 * user saw, not the UTC day.
 */
export function localDateForOffset(now: Date, offsetMinutes: number): string {
	const shifted = new Date(now.getTime() - offsetMinutes * 60_000);
	return shifted.toISOString().slice(0, 10);
}
